const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

class Polling {
  constructor({
    fetchFn,
    isDone,
    getDelay,
    maxAttempts,
    onUpdate,
    onDone,
    onDegraded,
    defaultDelay,
    minDelay,
    maxDelay,
    requestTimeoutMs,
  }) {
    this.fetchFn = fetchFn;
    this.isDone = isDone;
    this.getDelay = getDelay;
    this.maxAttempts = maxAttempts;
    this.onUpdate = onUpdate;
    this.onDone = onDone;
    this.onDegraded = onDegraded;
    this.defaultDelay = defaultDelay;
    this.minDelay = minDelay;
    this.maxDelay = maxDelay;
    this.requestTimeoutMs = requestTimeoutMs;

    this.attempts = 0;
    this.running = false;
    this.activeAbortController = null;
    this.pendingDelayTimeoutId = null;
    this.activeRequestTimeoutId = null;
  }

  isRunning() {
    return this.running;
  }

  start() {
    if (this.running) return;
    this.attempts = 0;
    this.running = true;
    this._scheduleNext(0);
  }

  stop() {
    if (!this.running && this.pendingDelayTimeoutId == null && this.activeAbortController == null) {
      return;
    }
    this.running = false;
    if (this.pendingDelayTimeoutId != null) {
      clearTimeout(this.pendingDelayTimeoutId);
      this.pendingDelayTimeoutId = null;
    }
    if (this.activeRequestTimeoutId != null) {
      clearTimeout(this.activeRequestTimeoutId);
      this.activeRequestTimeoutId = null;
    }
    if (this.activeAbortController) {
      this.activeAbortController.abort();
      this.activeAbortController = null;
    }
  }

  _scheduleNext(delay) {
    if (!this.running) return;
    this.pendingDelayTimeoutId = setTimeout(() => this._runOnce(), delay);
  }

  async _runOnce() {
    this.pendingDelayTimeoutId = null;
    if (!this.running) return;

    this.attempts += 1;
    const controller = new AbortController();
    this.activeAbortController = controller;

    let timedOut = false;
    this.activeRequestTimeoutId = setTimeout(() => {
      timedOut = true;
      this.activeRequestTimeoutId = null;
      controller.abort();
    }, this.requestTimeoutMs);

    let result;
    try {
      result = await this.fetchFn(controller.signal);
    } catch (err) {
      this._clearRequestTimeout();
      // An external stop() (e.g. the effect cleanup) sets running=false BEFORE
      // aborting, so its AbortError lands here and is swallowed silently.
      if (!this.running) return;
      // Otherwise this is a failed ATTEMPT — a request timeout or a network/HTTP
      // error. A single failure must NOT kill the whole polling: retry until the
      // attempt cap, then degrade with the last failure reason. The exception is
      // an error explicitly marked non-retryable (e.g. a permanent 4xx), which
      // degrades immediately since retrying cannot change the outcome.
      const reason = timedOut ? 'request-timeout' : 'fetch-error';
      const shouldRetry = err?.retryable !== false;
      if (!shouldRetry || this.attempts >= this.maxAttempts) {
        this._finishDegraded(reason);
        return;
      }
      this.activeAbortController = null;
      this._scheduleNext(this.defaultDelay);
      return;
    }
    this._clearRequestTimeout();

    this.activeAbortController = null;
    if (!this.running) return;

    this.onUpdate(result, this.attempts);

    if (this.isDone(result)) {
      this._finishDone(result);
      return;
    }

    if (this.attempts >= this.maxAttempts) {
      this._finishDegraded('max-attempts');
      return;
    }

    const rawDelay = this.getDelay(result);
    const candidate = rawDelay == null ? this.defaultDelay : Number(rawDelay);
    const safeDelay = Number.isFinite(candidate) ? candidate : this.defaultDelay;
    const delay = clamp(safeDelay, this.minDelay, this.maxDelay);
    this._scheduleNext(delay);
  }

  _finishDone(result) {
    this.running = false;
    this.activeAbortController = null;
    this.onDone(result);
  }

  _finishDegraded(reason) {
    this.running = false;
    if (this.activeAbortController) {
      this.activeAbortController = null;
    }
    this.onDegraded(reason);
  }

  _clearRequestTimeout() {
    if (this.activeRequestTimeoutId != null) {
      clearTimeout(this.activeRequestTimeoutId);
      this.activeRequestTimeoutId = null;
    }
  }
}

export default Polling;
